import { ModKeys } from "@intelligems/ig-types/fe-only/personalizations";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import {
  ExperienceModPreset,
  ExperienceStateIssue,
  initialExperienceInfoStateSlice,
} from "../types/experienceInfoState";

const experienceInfoStateSlice = rtk.createSlice({
  name: "experienceInfoState",
  initialState: initialExperienceInfoStateSlice,
  reducers: {
    resetExperienceInfoState: () => {
      return initialExperienceInfoStateSlice;
    },
    setStateIssues: (
      draft,
      action: PayloadAction<ExperienceStateIssue | ExperienceStateIssue[]>
    ) => {
      const issues = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      for (const issue of issues) {
        if (issue.type === "warning") {
          if (issue.error) {
            if (!draft.warnings.map((arr) => arr.key).includes(issue.key)) {
              draft.warnings.push({
                key: issue.key,
                customMessage: issue.customMessage,
                docLink: issue.docLink,
                openDialog: issue.openDialog,
              });
            }
          } else {
            const index = draft.warnings.findIndex(
              (err) => err.key === issue.key
            );
            if (index !== -1) {
              draft.warnings.splice(index, 1);
            }
          }
        } else {
          if (issue.error) {
            if (!draft.errors.map((arr) => arr.key).includes(issue.key)) {
              draft.errors.push({
                key: issue.key,
                customMessage: issue.customMessage,
                docLink: issue.docLink,
                openDialog: issue.openDialog,
              });
            }
          } else {
            const index = draft.errors.findIndex(
              (err) => err.key === issue.key
            );
            if (index !== -1) {
              draft.errors.splice(index, 1);
            }
          }
        }
      }
    },
    setAlertOpen: (draft, action: PayloadAction<{ value: boolean }>) => {
      draft.alertOpen = action.payload.value;
    },
    setModActivated: (draft, action: PayloadAction<{ key: ModKeys }>) => {
      const index = draft.activatedModArr.indexOf(action.payload.key);
      if (index > -1) {
        draft.activatedModArr.splice(index, 1);
      } else {
        draft.activatedModArr.push(action.payload.key);
      }
    },
    setModActivatedAtTop: (draft, action: PayloadAction<{ key: ModKeys }>) => {
      const index = draft.activatedModArr.indexOf(action.payload.key);
      if (index > -1) {
        draft.activatedModArr.splice(index, 1);
      } else {
        draft.activatedModArr.unshift(action.payload.key);
      }
    },
    setBatchModsActivated: (
      draft,
      action: PayloadAction<{ mods: ModKeys[] }>
    ) => {
      draft.activatedModArr = action.payload.mods;
    },
    addVariationMod: (
      draft,
      action: PayloadAction<{ variationId: string; mod: ModKeys }>
    ) => {
      const { variationId, mod } = action.payload;

      // Check if the object for the given variationId exists
      if (!draft.activatedVariationMods[variationId]) {
        // If it doesn't exist, create it
        draft.activatedVariationMods[variationId] = { mods: [] };
      }

      // Check if the mod is already present in the mods array
      if (!draft.activatedVariationMods[variationId].mods.includes(mod)) {
        draft.activatedVariationMods[variationId].mods.push(mod);
      }
    },
    addVariationModAtTop: (
      draft,
      action: PayloadAction<{ variationId: string; mod: ModKeys }>
    ) => {
      const { variationId, mod } = action.payload;
      // Check if the object for the given variationId exists
      if (!draft.activatedVariationMods[variationId]) {
        // If it doesn't exist, create it
        draft.activatedVariationMods[variationId] = { mods: [] };
      }

      // Check if the mod is already present in the mods array
      const index = draft.activatedVariationMods[variationId].mods.indexOf(mod);
      if (index > -1) {
        // Remove it from current position
        draft.activatedVariationMods[variationId].mods.splice(index, 1);
      }
      draft.activatedVariationMods[variationId].mods.unshift(mod);
    },
    removeVariationMod: (
      draft,
      action: PayloadAction<{ variationId: string; mod: ModKeys }>
    ) => {
      const { variationId, mod } = action.payload;

      // Check if the object for the given variationId exists
      if (!draft.activatedVariationMods[variationId]) {
        return draft; // Exit if the variationId is not present
      }

      // Filter out the mod from the mods array
      draft.activatedVariationMods[variationId].mods =
        draft.activatedVariationMods[variationId].mods.filter(
          (existingMod) => existingMod !== mod
        );
    },
    setBatchVariationModsActivated: (
      draft,
      action: PayloadAction<{ variationId: string; mods: ModKeys[] }>
    ) => {
      // Check if the variationId already exists in the draft state
      if (!draft.activatedVariationMods) {
        draft.activatedVariationMods = {
          variationId: { mods: [] },
        };
      }

      // Update the mods for the specific variationId
      draft.activatedVariationMods[action.payload.variationId] = {
        mods: action.payload.mods,
      };
    },
    setCreationModPreset: (
      draft,
      action: PayloadAction<ExperienceModPreset | null>
    ) => {
      draft.creationModPreset = action.payload;
    },
    setModsTabVariationId: (draft, action: PayloadAction<string | null>) => {
      draft.modsTabVariationId = action.payload;
    },
    setAllBatchVariationModsActivated: (
      draft,
      action: PayloadAction<{ [variationId: string]: ModKeys[] }>
    ) => {
      // Check if the variationId already exists in the draft state
      Object.assign(draft.activatedVariationMods, {
        ...action.payload,
      });
    },
  },
  selectors: {
    selectedAlertOpen: (state) => state.alertOpen,
    selectedStateErrors: (state) => state.errors,
    selectedStateWarnings: (state) => state.warnings,
    selectedActivatedModArray: (state) => state.activatedModArr,
    selectedOfferVariationMods: (state) => state.activatedVariationMods,
    selectedCreationModPreset: (state) => state.creationModPreset,
    selectedModsTabVariationId: (state) => state.modsTabVariationId,
  },
});

export const {
  resetExperienceInfoState,
  setAlertOpen,
  setStateIssues,
  setModActivated,
  setModActivatedAtTop,
  setBatchModsActivated,
  addVariationMod,
  addVariationModAtTop,
  removeVariationMod,
  setBatchVariationModsActivated,
  setAllBatchVariationModsActivated,
  setCreationModPreset,
  setModsTabVariationId,
} = experienceInfoStateSlice.actions;

export const experienceInfoStateReducer = experienceInfoStateSlice.reducer;

export const {
  selectedAlertOpen,
  selectedStateErrors,
  selectedStateWarnings,
  selectedActivatedModArray,
  selectedOfferVariationMods,
  selectedCreationModPreset,
  selectedModsTabVariationId,
} = experienceInfoStateSlice.selectors;
